import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, CircularProgress, Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

export interface FotoLightboxItem {
  url: string;
  caption: string | null;
  filename?: string;
}

interface FotoLightboxProps {
  open: boolean;
  onClose: () => void;
  fotos: FotoLightboxItem[];
  indiceActual: number;
  onIndiceChange: (nuevoIndice: number) => void;
}

export function FotoLightbox({
  open,
  onClose,
  fotos,
  indiceActual,
  onIndiceChange,
}: FotoLightboxProps): JSX.Element {
  const [imgStatus, setImgStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [isDownloading, setIsDownloading] = useState(false);

  const fotoActual = fotos[indiceActual];
  const total = fotos.length;
  const hayNavegacion = total > 1;

  // Reset image status on foto change
  useEffect(() => {
    setImgStatus('loading');
  }, [indiceActual, open]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'ArrowLeft' && indiceActual > 0) {
        onIndiceChange(indiceActual - 1);
      } else if (e.key === 'ArrowRight' && indiceActual < total - 1) {
        onIndiceChange(indiceActual + 1);
      } else if (e.key === 'Escape') {
        onClose();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, indiceActual, total, onIndiceChange, onClose]);

  async function handleDescargar(): Promise<void> {
    if (!fotoActual || isDownloading) return;
    setIsDownloading(true);
    try {
      const res = await fetch(fotoActual.url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fotoActual.filename ?? `foto-${indiceActual + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'rgba(0,0,0,0.92)',
          position: 'relative',
        }}
      >
        {/* Top bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {total > 0 ? `${indiceActual + 1} / ${total}` : ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Descargar">
              <span>
                <IconButton
                  onClick={() => void handleDescargar()}
                  disabled={isDownloading || !fotoActual}
                  sx={{ color: 'white' }}
                  size="small"
                >
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={onClose} sx={{ color: 'white' }} size="small" aria-label="Cerrar">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Image area */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {/* Loading spinner */}
          {imgStatus === 'loading' && fotoActual && (
            <CircularProgress sx={{ color: 'white', position: 'absolute' }} />
          )}

          {/* Error state */}
          {imgStatus === 'error' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                color: 'grey.500',
              }}
            >
              <BrokenImageIcon sx={{ fontSize: 64 }} />
              <Typography variant="body2">No se pudo cargar la imagen</Typography>
            </Box>
          )}

          {/* Image */}
          {fotoActual && (
            <Box
              component="img"
              src={fotoActual.url}
              alt={fotoActual.caption ?? 'Foto'}
              onLoad={() => setImgStatus('loaded')}
              onError={() => setImgStatus('error')}
              sx={{
                maxWidth: '90vw',
                maxHeight: 'calc(100vh - 160px)',
                objectFit: 'contain',
                display: imgStatus === 'loaded' ? 'block' : 'none',
              }}
            />
          )}

          {/* Prev button */}
          {hayNavegacion && (
            <IconButton
              onClick={() => onIndiceChange(indiceActual - 1)}
              disabled={indiceActual === 0}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 56,
                height: 56,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' },
              }}
            >
              <ChevronLeftIcon fontSize="large" />
            </IconButton>
          )}

          {/* Next button */}
          {hayNavegacion && (
            <IconButton
              onClick={() => onIndiceChange(indiceActual + 1)}
              disabled={indiceActual === total - 1}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 56,
                height: 56,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' },
              }}
            >
              <ChevronRightIcon fontSize="large" />
            </IconButton>
          )}
        </Box>

        {/* Footer — caption */}
        <Box
          sx={{
            flexShrink: 0,
            py: 1.5,
            px: 3,
            textAlign: 'center',
            minHeight: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {fotoActual?.caption ? (
            <Typography variant="body2" sx={{ color: 'white', fontSize: 14 }}>
              {fotoActual.caption}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: 'grey.600', fontSize: 14, fontStyle: 'italic' }}
            >
              Sin descripción
            </Typography>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
